/**
 * Local File Storage Module for GJYL Desktop
 * Uses IndexedDB to store P2P transferred files locally
 */

const DB_NAME = 'gjyl_files';
const DB_VERSION = 1;
const STORE_NAME = 'files';
const META_STORE = 'metadata';

// Expiration time: 3 months
const EXPIRATION_TIME = 90 * 24 * 60 * 60 * 1000;

/**
 * Initialize the IndexedDB database
 */
function initDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error('Failed to open local file storage'));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Store for file blobs
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const fileStore = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        fileStore.createIndex('createdAt', 'createdAt', { unique: false });
        fileStore.createIndex('conversationId', 'conversationId', { unique: false });
        fileStore.createIndex('userId', 'userId', { unique: false });
      }

      // Store for metadata only (for listing without loading full files)
      if (!db.objectStoreNames.contains(META_STORE)) {
        const metaStore = db.createObjectStore(META_STORE, { keyPath: 'id' });
        metaStore.createIndex('createdAt', 'createdAt', { unique: false });
        metaStore.createIndex('conversationId', 'conversationId', { unique: false });
        metaStore.createIndex('userId', 'userId', { unique: false });
        metaStore.createIndex('expiresAt', 'expiresAt', { unique: false });
      }
    };
  });
}

/**
 * Get a transaction for the given stores
 */
async function getTransaction(storeNames, mode = 'readonly') {
  const db = await initDB();
  return db.transaction(storeNames, mode);
}

export const localFileStorage = {
  /**
   * Save a file to local storage
   * @param {string} fileId - Unique file identifier
   * @param {File|Blob} file - The file to save
   * @param {Object} metadata - File metadata
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  save: async (fileId, file, metadata = {}) => {
    try {
      const db = await initDB();
      const transaction = db.transaction([STORE_NAME, META_STORE], 'readwrite');
      const fileStore = transaction.objectStore(STORE_NAME);
      const metaStore = transaction.objectStore(META_STORE);

      const now = Date.now();
      const expiresAt = now + EXPIRATION_TIME;

      // Store the file blob
      const fileRecord = {
        id: fileId,
        blob: file,
        createdAt: now,
        expiresAt,
        ...metadata
      };

      // Store metadata separately
      const metaRecord = {
        id: fileId,
        name: metadata.name || file.name || 'unknown',
        size: file.size,
        type: file.type || metadata.type || 'application/octet-stream',
        createdAt: now,
        expiresAt,
        conversationId: metadata.conversationId,
        userId: metadata.userId,
        source: metadata.source || 'upload',
        thumbnailDataUrl: metadata.thumbnailDataUrl || null
      };

      await Promise.all([
        new Promise((resolve, reject) => {
          const request = fileStore.put(fileRecord);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        }),
        new Promise((resolve, reject) => {
          const request = metaStore.put(metaRecord);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        })
      ]);

      return { success: true };
    } catch (error) {
      console.error('Error saving file to local storage:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get a file from local storage
   * @param {string} fileId - File identifier
   * @returns {Promise<{blob?: Blob, metadata?: Object, exists: boolean}>}
   */
  get: async (fileId) => {
    try {
      const db = await initDB();
      const transaction = db.transaction([STORE_NAME, META_STORE], 'readonly');
      const fileStore = transaction.objectStore(STORE_NAME);
      const metaStore = transaction.objectStore(META_STORE);

      const [fileRecord, metaRecord] = await Promise.all([
        new Promise((resolve, reject) => {
          const request = fileStore.get(fileId);
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        }),
        new Promise((resolve, reject) => {
          const request = metaStore.get(fileId);
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        })
      ]);

      if (!fileRecord) {
        return { exists: false };
      }

      return {
        exists: true,
        blob: fileRecord.blob,
        metadata: metaRecord || {}
      };
    } catch (error) {
      console.error('Error getting file from local storage:', error);
      return { exists: false, error: error.message };
    }
  },

  /**
   * Get file metadata without loading the blob
   * @param {string} fileId - File identifier
   * @returns {Promise<Object|null>}
   */
  getMetadata: async (fileId) => {
    try {
      const db = await initDB();
      const transaction = db.transaction([META_STORE], 'readonly');
      const metaStore = transaction.objectStore(META_STORE);

      return new Promise((resolve, reject) => {
        const request = metaStore.get(fileId);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error getting file metadata:', error);
      return null;
    }
  },

  /**
   * Delete a file from local storage
   * @param {string} fileId - File identifier
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  delete: async (fileId) => {
    try {
      const db = await initDB();
      const transaction = db.transaction([STORE_NAME, META_STORE], 'readwrite');
      const fileStore = transaction.objectStore(STORE_NAME);
      const metaStore = transaction.objectStore(META_STORE);

      await Promise.all([
        new Promise((resolve, reject) => {
          const request = fileStore.delete(fileId);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        }),
        new Promise((resolve, reject) => {
          const request = metaStore.delete(fileId);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        })
      ]);

      return { success: true };
    } catch (error) {
      console.error('Error deleting file from local storage:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Delete multiple files
   * @param {string[]} fileIds - Array of file identifiers
   * @returns {Promise<{success: boolean, deleted: number, error?: string}>}
   */
  deleteMany: async (fileIds) => {
    try {
      const db = await initDB();
      const transaction = db.transaction([STORE_NAME, META_STORE], 'readwrite');
      const fileStore = transaction.objectStore(STORE_NAME);
      const metaStore = transaction.objectStore(META_STORE);

      let deleted = 0;
      for (const fileId of fileIds) {
        await Promise.all([
          new Promise((resolve) => {
            const request = fileStore.delete(fileId);
            request.onsuccess = () => { deleted++; resolve(); };
            request.onerror = () => resolve();
          }),
          new Promise((resolve) => {
            const request = metaStore.delete(fileId);
            request.onsuccess = () => resolve();
            request.onerror = () => resolve();
          })
        ]);
      }

      return { success: true, deleted };
    } catch (error) {
      console.error('Error deleting files:', error);
      return { success: false, deleted: 0, error: error.message };
    }
  },

  /**
   * List all files with metadata
   * @param {Object} options - Filter options
   * @param {string} options.conversationId - Filter by conversation
   * @param {string} options.userId - Filter by user
   * @param {string} options.sortBy - Sort field ('createdAt', 'size', 'name')
   * @param {string} options.sortOrder - Sort order ('asc', 'desc')
   * @returns {Promise<Object[]>}
   */
  list: async (options = {}) => {
    try {
      const db = await initDB();
      const transaction = db.transaction([META_STORE], 'readonly');
      const metaStore = transaction.objectStore(META_STORE);

      const files = await new Promise((resolve, reject) => {
        const request = metaStore.getAll();
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      });

      // Apply filters
      let filtered = files;

      if (options.conversationId) {
        filtered = filtered.filter(f => f.conversationId === options.conversationId);
      }

      if (options.userId) {
        filtered = filtered.filter(f => f.userId === options.userId);
      }

      // Apply sorting
      const sortBy = options.sortBy || 'createdAt';
      const sortOrder = options.sortOrder || 'desc';

      filtered.sort((a, b) => {
        let comparison = 0;
        if (sortBy === 'size') {
          comparison = a.size - b.size;
        } else if (sortBy === 'name') {
          comparison = a.name.localeCompare(b.name);
        } else {
          comparison = a.createdAt - b.createdAt;
        }
        return sortOrder === 'asc' ? comparison : -comparison;
      });

      return filtered;
    } catch (error) {
      console.error('Error listing files:', error);
      return [];
    }
  },

  /**
   * Get total size of stored files
   * @returns {Promise<{totalSize: number, fileCount: number}>}
   */
  getTotalSize: async () => {
    try {
      const files = await localFileStorage.list();
      const totalSize = files.reduce((sum, file) => sum + (file.size || 0), 0);
      return { totalSize, fileCount: files.length };
    } catch (error) {
      console.error('Error getting total size:', error);
      return { totalSize: 0, fileCount: 0 };
    }
  },

  /**
   * Clear all stored files
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  clearAll: async () => {
    try {
      const db = await initDB();
      const transaction = db.transaction([STORE_NAME, META_STORE], 'readwrite');
      const fileStore = transaction.objectStore(STORE_NAME);
      const metaStore = transaction.objectStore(META_STORE);

      await Promise.all([
        new Promise((resolve, reject) => {
          const request = fileStore.clear();
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        }),
        new Promise((resolve, reject) => {
          const request = metaStore.clear();
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        })
      ]);

      return { success: true };
    } catch (error) {
      console.error('Error clearing all files:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Clean up expired files (older than 3 months)
   * @returns {Promise<{success: boolean, cleaned: number}>}
   */
  cleanupExpired: async () => {
    try {
      const now = Date.now();
      const files = await localFileStorage.list();
      const expiredIds = files
        .filter(f => f.expiresAt && f.expiresAt < now)
        .map(f => f.id);

      if (expiredIds.length > 0) {
        const result = await localFileStorage.deleteMany(expiredIds);
        return { success: true, cleaned: result.deleted };
      }

      return { success: true, cleaned: 0 };
    } catch (error) {
      console.error('Error cleaning up expired files:', error);
      return { success: false, cleaned: 0, error: error.message };
    }
  },

  /**
   * Check if a file exists
   * @param {string} fileId - File identifier
   * @returns {Promise<boolean>}
   */
  exists: async (fileId) => {
    const result = await localFileStorage.getMetadata(fileId);
    return result !== null;
  },

  /**
   * Create a downloadable URL for a stored file
   * @param {string} fileId - File identifier
   * @returns {Promise<string|null>}
   */
  createObjectUrl: async (fileId) => {
    try {
      const { blob, exists } = await localFileStorage.get(fileId);
      if (!exists || !blob) return null;
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error creating object URL:', error);
      return null;
    }
  },

  /**
   * Revoke a previously created object URL
   * @param {string} url - The URL to revoke
   */
  revokeObjectUrl: (url) => {
    if (url) {
      URL.revokeObjectURL(url);
    }
  }
};

// Run cleanup on initialization
if (typeof window !== 'undefined') {
  localFileStorage.cleanupExpired().catch(console.error);
}

export default localFileStorage;
